import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UsersService } from '../../services/users.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import type { VendorLocation, VendorRow, VendorUserGroupOption } from './vendors.model';

export interface ExistingLoginOption {
	userid: string;
	salesmancode: string;
	realname: string;
	blocked: boolean;
}

export interface VendorCreateLoginDialogData {
	row: VendorRow;
	usergroups: VendorUserGroupOption[];
	locations: VendorLocation[];
	/** Other brands under the same supplier — lets one login span all of a vendor's brands. */
	supplierBrands?: { manufacturer_id: number; brand_name: string }[];
	/** Brand-dedicated logins across ALL brands, for "link an existing login instead". */
	existingLogins?: ExistingLoginOption[];
}

@Component({
	selector: 'app-vendor-create-login-dialog',
	templateUrl: './vendor-create-login-dialog.component.html',
	styleUrls: ['./vendor-create-login-dialog.component.scss'],
})
export class VendorCreateLoginDialogComponent implements OnInit {
	saving = false;
	hidePassword = true;

	/** Which custom dropdown is expanded (only one at a time). */
	openMenu: 'group' | 'brands' | 'location' | 'existingLogin' | null = null;

	readonly compareGroupId = (a: unknown, b: unknown) => String(a ?? '') === String(b ?? '');
	readonly compareLoccode = (a: unknown, b: unknown) => String(a ?? '').trim() === String(b ?? '').trim();

	form = new FormGroup({
		mode: new FormControl<'new' | 'existing'>('new'),
		existingUserid: new FormControl(''),

		salesman: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{1,4}$/)]),
		userid: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+$/)]),
		password: new FormControl('', [Validators.required, Validators.minLength(4)]),
		email: new FormControl(''),
		user_group: new FormControl<string | number | ''>('', [Validators.required]),
		defaultlocation: new FormControl('', [Validators.required]),
		brand_ids: new FormControl<number[]>([]),
	});

	existingLoginFilter = new FormControl('');

	get supplierBrands(): { manufacturer_id: number; brand_name: string }[] {
		return this.data.supplierBrands || [];
	}

	get hasMultipleBrands(): boolean {
		return this.supplierBrands.length > 1;
	}

	get isExistingMode(): boolean {
		return this.form.get('mode')?.value === 'existing';
	}

	/** Only offer "link an existing login" once there's actually an existing login to pick. */
	get canOfferExisting(): boolean {
		return !!this.data.existingLogins?.length;
	}

	get existingLogins(): ExistingLoginOption[] {
		return this.data.existingLogins || [];
	}

	get filteredExistingLogins(): ExistingLoginOption[] {
		const term = (this.existingLoginFilter.value || '').trim().toLowerCase();
		const list = this.existingLogins;
		if (!term) {
			return list;
		}
		return list.filter((l) => `${l.userid} ${l.realname} ${l.salesmancode}`.toLowerCase().includes(term));
	}

	get selectedExistingLoginLabel(): string {
		const uid = this.form.get('existingUserid')?.value;
		const l = this.existingLogins.find((x) => x.userid === uid);
		if (!l) {
			return 'Select a login';
		}
		return `${l.userid} — ${l.realname} (${l.salesmancode})`;
	}

	setMode(mode: 'new' | 'existing'): void {
		this.form.get('mode')?.setValue(mode);
	}

	selectExistingLogin(l: ExistingLoginOption): void {
		this.form.get('existingUserid')?.setValue(l.userid);
		this.openMenu = null;
	}

	constructor(
		private dialogRef: MatDialogRef<VendorCreateLoginDialogComponent, { created: boolean; userid?: string }>,
		@Inject(MAT_DIALOG_DATA) public data: VendorCreateLoginDialogData,
		private usersService: UsersService,
		private globalSearchService: GlobalSearchService
	) {}

	get canSubmit(): boolean {
		if (this.isExistingMode) {
			return !!(this.form.get('existingUserid')?.value || '').trim();
		}
		return !!(this.data.usergroups?.length && this.data.locations?.length);
	}

	// --- Custom dropdowns (inline, no CDK overlay so they stay inside the modal) ---

	toggleMenu(menu: 'group' | 'brands' | 'location' | 'existingLogin'): void {
		this.openMenu = this.openMenu === menu ? null : menu;
	}

	/** Any click that reaches document (i.e. outside a .cs-field) closes menus. */
	@HostListener('document:click')
	closeMenus(): void {
		this.openMenu = null;
	}

	get selectedGroupLabel(): string {
		const id = this.form.get('user_group')?.value;
		const g = (this.data.usergroups || []).find((x) => this.compareGroupId(x.user_group_id, id));
		if (!g) {
			return 'Select a group';
		}
		return g.name + (g.user_count != null ? ` (${g.user_count} users)` : '');
	}

	isGroupSelected(g: VendorUserGroupOption): boolean {
		return this.compareGroupId(g.user_group_id, this.form.get('user_group')?.value);
	}

	selectGroup(g: VendorUserGroupOption): void {
		const ctrl = this.form.get('user_group');
		ctrl?.setValue(g.user_group_id);
		ctrl?.markAsTouched();
		this.openMenu = null;
	}

	get selectedLocationLabel(): string {
		const code = String(this.form.get('defaultlocation')?.value ?? '').trim();
		const loc = (this.data.locations || []).find((l) => this.compareLoccode(l.loccode, code));
		if (!loc) {
			return 'Select a location';
		}
		return `${loc.loccode} – ${loc.locationname || loc.orignalname || loc.loccode}`;
	}

	isLocationSelected(loc: VendorLocation): boolean {
		return this.compareLoccode(loc.loccode, this.form.get('defaultlocation')?.value);
	}

	selectLocation(loc: VendorLocation): void {
		const ctrl = this.form.get('defaultlocation');
		ctrl?.setValue(String(loc.loccode));
		ctrl?.markAsTouched();
		this.openMenu = null;
	}

	get selectedBrandLabel(): string {
		const ids = this.form.get('brand_ids')?.value || [];
		if (!ids.length) {
			return 'Select brands';
		}
		const names = this.supplierBrands.filter((b) => ids.includes(b.manufacturer_id)).map((b) => b.brand_name);
		return names.join(', ');
	}

	/** The clicked brand (portal default) is always included and can't be removed. */
	isBrandLocked(b: { manufacturer_id: number }): boolean {
		return b.manufacturer_id === this.data.row.manufacturer_id;
	}

	isBrandSelected(b: { manufacturer_id: number }): boolean {
		return (this.form.get('brand_ids')?.value || []).includes(b.manufacturer_id);
	}

	toggleBrand(b: { manufacturer_id: number }): void {
		if (this.isBrandLocked(b)) {
			return;
		}
		const ctrl = this.form.get('brand_ids');
		const current = [...(ctrl?.value || [])];
		const idx = current.indexOf(b.manufacturer_id);
		if (idx >= 0) {
			current.splice(idx, 1);
		} else {
			current.push(b.manufacturer_id);
		}
		ctrl?.setValue(current);
	}

	ngOnInit(): void {
		const row = this.data.row;
		const locs = this.data.locations || [];
		const groups = this.data.usergroups || [];

		const groupId = this.defaultSalesmanGroupId() ?? groups[0]?.user_group_id ?? '';
		const initialLoc = locs[0]?.loccode != null ? String(locs[0].loccode) : '';

		this.form.patchValue({
			salesman: (row.suggested_salesmancode || '').toUpperCase(),
			userid: (row.suggested_userid || '').toLowerCase(),
			password: '',
			email: '',
			user_group: groupId,
			defaultlocation: initialLoc,
			brand_ids: [row.manufacturer_id],
		});

		if (!locs.length) {
			this.form.get('defaultlocation')?.clearValidators();
			this.form.get('defaultlocation')?.updateValueAndValidity();
		}
	}

	cancel(): void {
		this.dialogRef.close({ created: false });
	}

	generatePassword(): void {
		const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
		let pass = '';
		for (let i = 0; i < 8; i++) {
			pass += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		this.form.get('password')?.setValue(pass);
	}

	submit(): void {
		if (this.isExistingMode) {
			this.submitExisting();
			return;
		}
		if (!this.canSubmit || this.form.invalid || this.saving) {
			this.form.markAllAsTouched();
			return;
		}
		const row = this.data.row;
		const v = this.form.getRawValue();
		const userid = (v.userid || '').trim().toLowerCase();
		const salesmancode = (v.salesman || '').trim().toUpperCase();
		const displayName = row.brand_name || row.supplier_name;

		// One-shot brand-login create. createsalesperson writes www_users + salesman +
		// salesman_manufacturer (with supplier_id + default_for_portal=1) atomically, sets
		// fullaccess=5 and allowed_client_configs, and rejects if the brand already has a
		// login — so we never re-create the inconsistent hand-rolled rows the old
		// createUser path produced (NULL supplier_id, NULL configs).
		// Primary brand is the clicked row (portal default); brand_ids carries the full
		// set so one login can span all of a vendor's brands, each with its supplier_id.
		const selectedBrandIds = (v.brand_ids || []).map((n) => Number(n)).filter((n) => n > 0);
		const brandIds = Array.from(new Set<number>([row.manufacturer_id, ...selectedBrandIds]));
		const payload: Record<string, unknown> = {
			manufacturer_id: row.manufacturer_id,
			manufacturer_ids: brandIds,
			userid,
			password: v.password,
			realname: displayName,
			email: (v.email || '').trim(),
			salesmancode,
			user_group: v.user_group,
			defaultlocation: String(v.defaultlocation || '').trim(),
		};

		this.saving = true;
		this.usersService.createSalesperson(payload).subscribe({
			next: (res: any) => {
				this.saving = false;
				if (res?.success) {
					this.dialogRef.close({ created: true, userid });
				} else {
					this.globalSearchService.showNotification(res?.message || 'Create failed', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.saving = false;
				const msg = err?.error?.message || err?.message || 'Create failed';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			},
		});
	}

	/** "Link existing login" mode — just adds a salesman_manufacturer row, no new www_users. */
	private submitExisting(): void {
		if (!this.canSubmit || this.saving) {
			return;
		}
		const row = this.data.row;
		const userid = (this.form.get('existingUserid')?.value || '').trim();

		this.saving = true;
		this.usersService.linkExistingLogin({ manufacturer_id: row.manufacturer_id, userid }).subscribe({
			next: (res: any) => {
				this.saving = false;
				if (res?.success) {
					this.dialogRef.close({ created: true, userid });
				} else {
					this.globalSearchService.showNotification(res?.message || 'Could not link login', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.saving = false;
				const msg = err?.error?.message || err?.message || 'Could not link login';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			},
		});
	}

	private defaultSalesmanGroupId(): string | number | null {
		const match = (this.data.usergroups || []).find((g) => (g?.name || '').toLowerCase() === 'salesman');
		if (match?.user_group_id == null || match.user_group_id === '') {
			return null;
		}
		return match.user_group_id;
	}
}
